/**
 * The workspace SLOT MODEL (#355/#356/#358/#359/#360/#361).
 *
 * The workspace holds AT MOST TWO occupants side by side. An occupant is either
 * the Main Chat or a feature panel — the chat is a closable occupant, NOT the
 * permanent anchor it used to be. Everything here is pure: the state is derived
 * from the URL and every transition returns a new state, so the rules can be
 * unit-tested without a router, a DOM or a shell.
 *
 * ── The two rules that make the model predictable ────────────────────────────
 *
 *  1. **The LEFT slot is sticky; the RIGHT slot is the working slot.** Opening a
 *     feature always lands it in the right slot, replacing whatever feature was
 *     there. The chat, when open, always holds the left slot — which is exactly
 *     the pre-existing behaviour (chat left, one panel right, the panel swapping
 *     as you click around the sidebar). Closing the chat frees the left slot, and
 *     only then can a SECOND feature move in (#361).
 *
 *  2. **Display order is [left, right].** `occupantOrder()` is the single source
 *     of truth for what renders where, so the shell never re-derives it.
 *
 * ── URL encoding (back-compatible by construction) ───────────────────────────
 *
 *   /                      → chat alone            (≈150 specs navigate here)
 *   /chat/:id              → chat alone, one thread
 *   /boards                → chat + boards         (the pre-existing split)
 *   /boards?chat=0         → boards alone          (a launcher tile, #356)
 *   /boards?with=expenses  → expenses | boards     (two features, #361)
 *
 * The pathname always names the RIGHT occupant, so a deep link (`/boards?board=…`,
 * `/telegram?day=…`) keeps addressing the feature it always did. `with` names the
 * left one; `chat=0` says the left slot is deliberately empty.
 *
 * Expansion (#359) is per-occupant: `view=expanded` keeps its historical meaning
 * ("the pathname occupant is expanded") and `expand=<name>` is only written when
 * the LEFT occupant is the expanded one — so every existing `?view=expanded` link
 * and spec still means what it meant.
 */

export const CHAT = 'chat';
export const MAX_OCCUPANTS = 2;

/** Map of URL path prefix → logical panel name. */
export const PATH_TO_PANEL = {
    '/calendar': 'calendar',
    '/expenses': 'expenses',
    '/agenda': 'agenda',
    '/boards': 'boards',
    '/telegram': 'telegram',
    '/notes': 'notes',
    // Reminders & Notifications (#437) — split out of the old Notes & Reminders.
    '/reminders': 'reminders',
    '/rulebook': 'rulebook',
    '/energy': 'energy',
};

export const PANEL_TO_PATH = Object.fromEntries(
    Object.entries(PATH_TO_PANEL).map(([path, panel]) => [panel, path]),
);

export const PANEL_NAMES = Object.values(PATH_TO_PANEL);

/** The query params this module owns. Anything else on a URL is passed through. */
export const CONTROL_PARAMS = ['with', 'chat', 'view', 'expand'];

export const isFeature = (name) => typeof name === 'string' && PANEL_NAMES.includes(name);

export function panelForPath(pathname) {
    for (const [prefix, name] of Object.entries(PATH_TO_PANEL)) {
        if (pathname === prefix || pathname.startsWith(prefix + '/')) return name;
    }
    return null;
}

export const pathForPanel = (name) => PANEL_TO_PATH[name] || null;

/**
 * Clamp a candidate state onto the model's invariants:
 *   • features are real, de-duplicated, at most two;
 *   • an open chat leaves room for only ONE feature (the most recent);
 *   • `expanded` names an occupant that is actually open (the chat never expands —
 *     it has no expand control, and "expanded" means covering the chat's own
 *     conversation column).
 */
export function normalizeWorkspace(state) {
    const seen = new Set();
    let features = (state?.features || []).filter((f) => {
        if (!isFeature(f) || seen.has(f)) return false;
        seen.add(f);
        return true;
    });
    const chatOpen = !!state?.chatOpen;
    features = features.slice(chatOpen ? -1 : -MAX_OCCUPANTS);
    const expanded = features.includes(state?.expanded) ? state.expanded : null;
    return { chatOpen, features, expanded };
}

/** Display order, left → right. The chat, when open, is always leftmost. */
export function occupantOrder(state) {
    const s = normalizeWorkspace(state);
    return [...(s.chatOpen ? [CHAT] : []), ...s.features];
}

export const isEmptyWorkspace = (state) => occupantOrder(state).length === 0;

export const isOccupantOpen = (state, name) => (
    name === CHAT ? !!state?.chatOpen : (state?.features || []).includes(name)
);

/* ── Transitions ─────────────────────────────────────────────────────────── */

export function openOccupant(state, name) {
    const s = normalizeWorkspace(state);
    if (name === CHAT) {
        if (s.chatOpen) return s;
        // The chat takes the LEFT slot, so it evicts a left-slot feature and keeps
        // whatever sits in the working slot.
        return normalizeWorkspace({ ...s, chatOpen: true, features: s.features.slice(-1) });
    }
    if (!isFeature(name) || s.features.includes(name)) return s;
    let features;
    if (s.chatOpen) features = [name];                              // chat sticky → the feature slot swaps
    else if (s.features.length < MAX_OCCUPANTS) features = [...s.features, name];
    else features = [s.features[0], name];                          // left sticky → the right slot swaps
    return normalizeWorkspace({ ...s, features });
}

export function closeOccupant(state, name) {
    const s = normalizeWorkspace(state);
    if (name === CHAT) return normalizeWorkspace({ ...s, chatOpen: false });
    return normalizeWorkspace({ ...s, features: s.features.filter((f) => f !== name) });
}

export function toggleOccupant(state, name) {
    return isOccupantOpen(normalizeWorkspace(state), name)
        ? closeOccupant(state, name)
        : openOccupant(state, name);
}

/** Expand one occupant so it covers everything right of the Features Sidebar (#359). */
export function setExpandedOccupant(state, name) {
    const s = normalizeWorkspace(state);
    return normalizeWorkspace({ ...s, expanded: name || null });
}

/* ── URL ↔ state ─────────────────────────────────────────────────────────── */

const asParams = (search) => (
    search instanceof URLSearchParams ? new URLSearchParams(search.toString()) : new URLSearchParams(search || '')
);

export function decodeWorkspace(pathname, search) {
    const params = asParams(search);
    const right = panelForPath(pathname || '/');
    if (!right) return { chatOpen: true, features: [], expanded: null };

    const withParam = params.get('with');
    const left = isFeature(withParam) && withParam !== right ? withParam : null;
    // Two features fill both slots, so `with` implies the chat is closed.
    const chatOpen = left ? false : params.get('chat') !== '0';
    const features = left ? [left, right] : [right];

    let expanded = params.get('expand');
    if (!features.includes(expanded)) expanded = null;
    if (!expanded && params.get('view') === 'expanded') expanded = right;

    return normalizeWorkspace({ chatOpen, features, expanded });
}

/**
 * State → { pathname, search }. `chatPath` is where a chat-only workspace lands
 * (`/chat/<id>` when a conversation should be restored); `baseParams` are the
 * non-control params to carry over (deep links, `?view` handled here).
 *
 * An EMPTY workspace has no view to render, so it resolves to the launcher —
 * "closing the last open thing routes to the Main Page" (#361).
 */
export function encodeWorkspace(state, { chatPath = '/', baseParams = null } = {}) {
    const { chatOpen, features, expanded } = normalizeWorkspace(state);
    const params = asParams(baseParams);
    CONTROL_PARAMS.forEach((k) => params.delete(k));

    if (!chatOpen && features.length === 0) {
        return { pathname: '/home', search: '', home: true };
    }
    if (features.length === 0) {
        return { pathname: chatPath || '/', search: params.toString(), home: false };
    }

    const right = features[features.length - 1];
    const left = features.length > 1 ? features[0] : null;
    if (left) params.set('with', left);
    else if (!chatOpen) params.set('chat', '0');
    if (expanded) {
        params.set('view', 'expanded');
        if (expanded !== right) params.set('expand', expanded);
    }
    return { pathname: pathForPanel(right), search: params.toString(), home: false };
}

/** `{pathname, search}` → the string react-router's navigate() wants. */
export const toUrl = ({ pathname, search }) => (search ? `${pathname}?${search}` : pathname);
